import on from 'dojo/on';
import { html, render } from 'lit-html';

import Mediator from 'gisw/Classes/Mediator';
// @ts-ignore
import auth from 'gisw/Helpers/auth';
import GWindowList from 'gisw/Widgets/GWindowList';
import PaymentSearch from 'gisw/Widgets/PaymentSearch/PaymentSearch';

class RightMenu {
  domNode = document.createElement('div');

  /**
   * @param {object} config
   * @param {string} config.id
   */
  constructor (config) {
    this.domNode.id = config.id;

    this.gWindowList = new GWindowList({ id: 'RightList', resizable: true });

    this.hasResultsPanel = false;
    this.salesSearch = null;
    this.instSearch = null;
    this.quickSearch = null;
    this.PaymentSearch = null;

    this.loadingContainer =
      document.getElementById('loadingContainer') ?? createLoadingContainer();

    // hide the loading container first
    this.hideWaitCursor();

    const helpContainer = document.getElementById('helpButtonContainer');
    // Empty `helpContainer`
    helpContainer.innerHTML = '';

    if (auth.siteHasLogin) {
      Mediator.login.placeAt(helpContainer);
    }

    if (Mediator.helppage) {
      Mediator.helppage.placeAt(helpContainer);
    }

    if (!Mediator.serverData.hasAdminTools) {
      return;
    }

    if (Mediator.toolsManager.hasAdminTools()) {
      createAdminButton(helpContainer);
    } else if (Mediator.currentUser && Mediator.currentUser !== '') {
      createProfileButton(helpContainer);
    }
  }

  async lazyLoad () {
    const buttonContainer = document.getElementById('loginButtonContainer');

    this.searchBar = await Mediator.Factory.Create({
      name: 'GenericSearchBar',
      params: { id: 'searchBar' }
    });

    buttonContainer.append(this.searchBar.domNode, this.gWindowList.domNode);
  }

  destroy () {
    this.gWindowList.destroy();

    if (this.searchBar) {
      this.searchBar.destroy();
    }

    if (this.salesSearch) {
      this.salesSearch.destroy();
    }

    if (this.instSearch) {
      this.instSearch.destroy();
    }

    if (this.quickSearch) {
      this.quickSearch.destroy();
    }
  }

  /**
   * @param {object} [options]
   * @param {boolean} [options.keepInstrumentSearch]
   * @param {boolean} [options.keepSalesSearch]
   * @param {boolean} [options.keepPaymentSearch]
   * @param {boolean} [options.keepQuickSearch]
   */
  clearList (options = {}) {
    this.gWindowList.remove('resultsPanel');
    this.hasResultsPanel = false;

    this.gWindowList.remove('resultsSidePanel');

    if (!options.keepInstrumentSearch) {
      this.gWindowList.remove('instrumentSearch');
    }

    if (!options.keepSalesSearch) {
      this.gWindowList.remove('salesSearch');
    }

    if (!options.keepPaymentSearch) {
      this.gWindowList.remove('PaymentSearch');
    }

    if (!options.keepQuickSearch) {
      this.gWindowList.remove('quickSearch');
    }
  }

  /**
   * @param {HTMLElement} [node]
   * @param {boolean} [clearMap]
   */
  displayResults (node, clearMap = true) {
    this.gWindowList.remove('resultsSidePanel');

    // Added to fix "empty" looking display panel when performing
    // very rapid search or identify
    if (this.hasResultsPanel) {
      this.gWindowList.remove('resultsPanel');
      this.hasResultsPanel = false;
    }

    // We passed in a node we want to put in the results slot
    if (!node) {
      /** @type {HTMLElement} */
      let resultNode;
      const results = Mediator.resultsPanel.results;

      if (results && results.numResults() === 1) {
        resultNode = Mediator.resultsPanel.sidePanel.domNode;

        const layerName = results.categories[0];
        const result = results.results[layerName][0];
        result.layername = layerName;
        Mediator.resultsPanel.sidePanel.setResult(result).then(() => {
          this.gWindowList.refresh();
        });
      } else if (results === null) {
        resultNode = Mediator.resultsPanel.sidePanel.domNode;
      } else {
        resultNode = Mediator.resultsPanel.domNode;
      }

      // highlight all features and display results panel when done
      Mediator.resultsPanel.showAllOnMap(clearMap, () => {
        // changing !this.hasResultsPanel to true here
        // also fix the search result toggle issue.
        if (!this.hasResultsPanel) {
          this.gWindowList.remove('resultsPanel');

          if (Mediator.inMobileMode) {
            if (!Mediator.mobileSearchVisible) {
              Mediator.showMobileRight();
            }

            this.gWindowList.add([
              {
                id: 'resultsPanel',
                key: 'resultsPanel',
                title: 'Results',
                'class': 'toolGWindow ResultsPanelGWindow',
                content: resultNode,
                showCloseButton: false
              }
            ]);
          } else {
            this.gWindowList.add([
              {
                id: 'resultsPanel',
                key: 'resultsPanel',
                title: 'Results',
                'class': 'toolGWindow ResultsPanelGWindow',
                content: resultNode,
                showCloseButton: false
              }
            ]);
          }

          this.hasResultsPanel = true;
        } else {
          /** @type {import('gisw/Widgets/GWindow').default} */
          const resultsPanel = this.gWindowList.getGWindow('resultsPanel');

          if (Mediator.inMobileMode) {
            if (!Mediator.mobileSearchVisible) {
              Mediator.showMobileRight();
            }
          }

          resultsPanel.setContent(resultNode);
        }

        /** @type {import('gisw/Widgets/GWindow').default} */
        const resultsPanel = this.gWindowList.getGWindow('resultsPanel');

        this.gWindowList.toggleKey('resultsPanel');
        resultsPanel.refresh();
      });
    } else {
      if (!this.hasResultsPanel) {
        if (Mediator.isMobile) {
          if (!Mediator.mobileSearchVisible) {
            Mediator.showMobileRight();
          }

          this.gWindowList.add([
            {
              id: 'resultsPanel',
              key: 'resultsPanel',
              title: 'Results',
              'class': 'toolGWindow ResultsPanelGWindow',
              content: node,
              showCloseButton: false
            }
          ]);
        } else {
          this.gWindowList.add([
            {
              id: 'resultsPanel',
              key: 'resultsPanel',
              title: 'Results',
              'class': 'toolGWindow ResultsPanelGWindow',
              content: node,
              showCloseButton: false
            }
          ]);
        }

        this.hasResultsPanel = true;
      } else {
        this.gWindowList.getGWindow('resultsPanel').setContent(node);
      }

      this.gWindowList.toggleKey('resultsPanel');
      this.gWindowList.getGWindow('resultsPanel').refresh();
    }
  }

  hideResults () {
    /** @type {import('gisw/Widgets/GWindow').default} */
    const resultsSidePanel = this.gWindowList.getGWindow('resultsSidePanel');

    if (resultsSidePanel) {
      this.gWindowList.remove('resultsSidePanel', { preventDestruction: true });
      resultsSidePanel.isMinimized = true;
    }

    return this.gWindowList.closeAllOpened();
  }

  /**
   * @param {HTMLElement} node
   * @param {Partial<CSSStyleDeclaration>} [positionInput]
   * @param {boolean} [keepOpen]
   */
  addSubResult (node, positionInput = {}, keepOpen = false) {
    /** @type {import('gisw/Widgets/GWindow').default} */
    const gwin = this.gWindowList.getGWindow('resultsSidePanel');

    if (!gwin) {
      if (!keepOpen) {
        this.gWindowList.closeAllOpened();
      }

      if (Mediator.inMobileMode) {
        if (!Mediator.mobileSearchVisible) {
          Mediator.showMobileRight();
        }

        this.gWindowList.add([
          {
            id: 'resultsSidePanel',
            key: 'resultsSidePanel',
            title: 'Details',
            'class': 'toolGWindow ResultsPanelGWindow',
            content: node,
            showCloseButton: true,
            autoResize: true
          }
        ]);
      } else {
        this.gWindowList.add([
          {
            id: 'resultsSidePanel',
            key: 'resultsSidePanel',
            title: 'Details',
            'class': 'toolGWindow ResultsPanelGWindow',
            content: node,
            showCloseButton: true
          }
        ]);
      }

      this.gWindowList.toggleKey('resultsSidePanel');
      setTimeout(function () {
        if (document.getElementById('Parcel Information')) {
          document.getElementById('Parcel Information').click();
        }
      }, 500);
    } else {
      this.gWindowList.closeAllOpened();
      gwin.setContent(node);
      this.gWindowList.toggleKey('resultsSidePanel');
    }
  }

  /**
   * Show loading text and icon
   */
  showWaitCursor () {
    this.loadingContainer.style.visibility = 'visible';
  }

  /**
   * Hide loading text and icon
   */
  hideWaitCursor () {
    this.loadingContainer.style.visibility = 'hidden';
  }

  /**
   * @param {string} curTab
   */
  async populateAdvancedSearch (curTab) {
    const tabTools = Mediator.serverData.TabTools[curTab];
    this.searchBar.clearSearchOptions();

    let hasAdvancedSearch = false;

    for (const tool of tabTools) {
      if (tool.Name === 'InstrumentSearch') {
        if (this.instSearch === null) {
          // Lazy loads the InstrumentSearch class
          this.instSearch = await Mediator.Factory.Create({
            name: 'InstrumentSearch',
            params: {}
          });
        }

        if (this.instSearch.isLoaded) {
          this.createInstrumentSearch();
        } else {
          on.once(this.instSearch, 'load', () => {
            this.createInstrumentSearch();
          });
        }

        hasAdvancedSearch = true;
      }

      if (tool.Name === 'SalesSearch') {
        if (this.salesSearch === null) {
          this.salesSearch = await Mediator.Factory.Create({
            name: 'SalesSearch',
            params: {}
          });
        }

        this.gWindowList.add([
          {
            id: 'salesSearch',
            key: 'salesSearch',
            title: 'Sales Search',
            content: this.salesSearch.domNode
          }
        ]);

        this.gWindowList.getGWindow('salesSearch').domNode.style.display =
          'none';
        this.searchBar.addSearchOption({
          value: 'salesSearch',
          display: 'Sales Search'
        });

        hasAdvancedSearch = true;
      }

      if (tool.Name === 'PaymentSearch') {
        if (this.PaymentSearch === null) {
          this.PaymentSearch = new PaymentSearch();
        }

        this.PaymentSearch.isWindowOpen = false;

        this.gWindowList.add([
          {
            id: 'PaymentSearch',
            key: 'PaymentSearch',
            title: 'Payment Search',
            content: this.PaymentSearch.getDomNode()
          }
        ]);

        const node = this.gWindowList.getGWindow('PaymentSearch').domNode;
        node.style.display = 'none';

        if (
          !Mediator.toolsManager.hasTool(
            'PersonalProperty',
            Mediator.getCurrentTabName()
          )
        ) {
          $('#SelectedPaymentType', node).find('option[value=2]').remove();
        }

        this.searchBar.addSearchOption({
          value: 'PaymentSearch',
          display: 'Payment Search',
          className: 'standard-search-button'
        });

        hasAdvancedSearch = true;

        $('#SubmitPaymentSearch').on('click', () => {
          this.PaymentSearch.onSubmitPaymentSearch();
        });
      }

      if (tool.Name === 'TaxCart') {
        const cartButton = document.createElement('button');
        cartButton.id = 'PaymentCartBtn';
        cartButton.classList.add('standard-search-button');
        cartButton.type = 'button';

        cartButton.addEventListener(
          'click',
          () => {
            Mediator.handle({ name: 'cart.panel.show' });
          },
          { passive: true }
        );

        render(
          html`<span
              class="FunctionButtonGlyph glyphicon glyphicon-shopping-cart"
              aria-hidden="true"
            ></span>
            <div id="CartTotalLabel">$0.00</div>`,
          cartButton
        );

        this.searchBar.searchOptions.prepend(cartButton);
      }

      if (tool.Name.toString().toLowerCase() === 'quicksearch') {
        if (this.quickSearch === null) {
          this.quickSearch = await Mediator.Factory.Create({
            name: 'QuickSearch',
            params: {}
          });
        }

        this.gWindowList.add([
          {
            id: 'quickSearch',
            key: 'quickSearch',
            title: 'Quick Search',
            content: this.quickSearch.domNode
          }
        ]);

        const quickSearchGWindow = this.gWindowList.getGWindow('quickSearch');

        quickSearchGWindow.domNode.style.display = 'none';
        quickSearchGWindow.domNode.addEventListener(
          'toggle',
          /** @param {CustomEvent} event */
          event => {
            if (event.detail.show) {
              // Reboot quickSearch when opening it again
              this.quickSearch.bootUp();
            }
          }
        );

        this.searchBar.addSearchOption({
          value: 'quickSearch',
          display: 'Quick Search'
        });

        hasAdvancedSearch = true;
      }
    }

    const hasReportBuilder = Mediator.toolsManager.hasTool(
      'ReportBuilder',
      Mediator.getCurrentTabName()
    );

    if (hasReportBuilder) {
      const searchOptionBtn = this.searchBar.addSearchOption({
        value: 'reportBuilder',
        display: 'Report Builder'
      });
      $(searchOptionBtn).on('click', () => {
        Mediator.toolsManager.buttonHandler(null, 'ReportBuilder', null);
      });

      hasAdvancedSearch = true;
    }

    this.searchBar.domNode.className = hasAdvancedSearch
      ? 'advanced'
      : 'simple';
  }

  createInstrumentSearch () {
    this.gWindowList.add([
      {
        id: 'instrumentSearch',
        key: 'instrumentSearch',
        title: 'Instrument Search',
        content: this.instSearch.domNode
      }
    ]);

    this.searchBar.addSearchOption({
      value: 'instrumentSearch',
      display: 'Instrument Search'
    });

    this.searchBar.showSearchOption(
      this.gWindowList.getGWindow('instrumentSearch')
    );
  }
}

function createLoadingContainer () {
  const loadingContainer = document.createElement('div');
  loadingContainer.id = 'loadingContainer';

  render(
    html`<div id="loadingImg"></div>
      <p id="loadingText">Loading</p>`,
    loadingContainer
  );

  document.getElementById('loadingContainerMain').appendChild(loadingContainer);

  return loadingContainer;
}

/**
 * Insert psuedo-link in the `domNode`
 * @param {HTMLElement} domNode
 */
function createAdminButton (domNode) {
  const button = document.createElement('button');

  button.id = 'adminButton';
  button.classList.add('help-container-button');
  button.type = 'button';
  button.textContent = 'Admin Page';

  button.addEventListener('click', () => {
    window.open(
      location.origin + location.pathname + 'Admin/' + location.search,
      '_blank'
    );
  });

  domNode.appendChild(button);
}

/**
 * @param {HTMLElement} domNode
 */
function createProfileButton (domNode) {
  const button = document.createElement('button');
  button.id = 'profileButton';
  button.type = 'button';
  button.style.textIndent = '0px';
  button.textContent = 'Profile';

  button.addEventListener('click', () => {
    window.open(location.origin + location.pathname + 'Profile/', '_blank');
  });

  domNode.appendChild(button);
}

export default RightMenu;
